/* Superdil redesign — scroll infinit pentru grile de produse (vanilla JS).
 *
 * Markup așteptat (vezi sections/bestsellers-infinite.liquid):
 *   [data-sd-infinite data-section-id data-next-url data-page data-fallback-url? data-fallback-section?]
 *     [data-sd-grid] > li[data-product-id]
 *     [data-sd-sentinel] [data-sd-skeletons] [data-sd-status] [data-sd-end] [data-sd-more]
 *
 * - IntersectionObserver pe santinelă, cu ~600px înainte de capătul grilei
 * - fetch pe URL-ul paginii următoare + section_id (Section Rendering API); parametrii existenți
 *   (sort_by, filtre) sunt deja în paginate.next.url și merg mai departe
 * - deduplicare după data-product-id; skeleton loaders; mesaj final „Ai văzut toate produsele”
 * - dacă paginarea de pe pagina curentă nu aduce produse noi, trece pe data-fallback-url (colecția)
 * - fără JS: rămâne butonul „Încarcă mai multe” + paginarea din <noscript>
 */
(() => {
  'use strict';

  class SdInfinite {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-sd-grid]');
      this.sentinel = root.querySelector('[data-sd-sentinel]');
      this.skeletons = root.querySelector('[data-sd-skeletons]');
      this.status = root.querySelector('[data-sd-status]');
      this.end = root.querySelector('[data-sd-end]');
      this.moreWrap = root.querySelector('[data-sd-more-wrap]');
      this.more = root.querySelector('[data-sd-more]');

      this.sectionId = root.dataset.sectionId;
      this.nextUrl = root.dataset.nextUrl || '';
      this.page = parseInt(root.dataset.page || '1', 10);
      this.fallbackUrl = root.dataset.fallbackUrl || '';
      this.fallbackSection = root.dataset.fallbackSection || '';
      this.usingFallback = false;
      this.loading = false;

      if (!this.grid) return;
      this.seen = new Set(
        Array.from(this.grid.querySelectorAll(':scope > [data-product-id]')).map((el) => el.dataset.productId)
      );

      if (this.more) {
        this.more.addEventListener('click', (event) => {
          event.preventDefault();
          this.load();
        });
      }

      if (!this.nextUrl) {
        this.finish(false);
        return;
      }

      if ('IntersectionObserver' in window && this.sentinel) {
        if (this.moreWrap) this.moreWrap.hidden = true;
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.load();
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        this.observer.observe(this.sentinel);
      }
    }

    withSection(url, sectionId) {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', sectionId);
      return u.toString();
    }

    async fetchPage(url) {
      const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const root = doc.querySelector('[data-sd-infinite]');
      if (!root) return { items: [], next: '' };
      const grid = root.querySelector('[data-sd-grid]');
      return {
        items: grid ? Array.from(grid.querySelectorAll(':scope > [data-product-id]')) : [],
        next: root.dataset.nextUrl || '',
      };
    }

    fallbackPageUrl(page) {
      const u = new URL(this.fallbackUrl, window.location.origin);
      u.searchParams.set('page', String(page));
      return this.withSection(u.toString(), this.fallbackSection);
    }

    async load() {
      if (this.loading || !this.nextUrl) return;
      this.loading = true;
      this.toggleSkeletons(true);

      try {
        const nextPage = this.page + 1;
        let result = await this.fetchPage(
          this.usingFallback ? this.fallbackPageUrl(nextPage) : this.withSection(this.nextUrl, this.sectionId)
        );
        let fresh = result.items.filter((li) => !this.seen.has(li.dataset.productId));

        // Paginarea pe pagina curentă a întors aceleași produse => încercăm colecția direct.
        if (!fresh.length && result.items.length && !this.usingFallback && this.fallbackUrl && this.fallbackSection) {
          this.usingFallback = true;
          result = await this.fetchPage(this.fallbackPageUrl(nextPage));
          fresh = result.items.filter((li) => !this.seen.has(li.dataset.productId));
        }

        fresh.forEach((li) => {
          this.seen.add(li.dataset.productId);
          this.grid.appendChild(document.adoptNode(li));
        });

        this.page = nextPage;
        this.nextUrl = result.next || '';
        if (this.status && fresh.length) this.status.textContent = `Am încărcat încă ${this.countText(fresh.length)}.`;
        if (!this.nextUrl || !result.items.length) this.finish(true);
      } catch (error) {
        this.fail();
      } finally {
        this.loading = false;
        this.toggleSkeletons(false);
      }
    }

    countText(n) {
      if (n === 1) return '1 produs';
      const rest = n % 100;
      return rest === 0 || rest >= 20 ? `${n} de produse` : `${n} produse`;
    }

    toggleSkeletons(show) {
      if (this.skeletons) this.skeletons.hidden = !show;
    }

    finish(showEnd) {
      if (this.observer) this.observer.disconnect();
      this.nextUrl = '';
      if (this.moreWrap) this.moreWrap.hidden = true;
      if (this.end && showEnd) this.end.hidden = false;
      this.root.classList.add('is-done');
    }

    fail() {
      if (this.observer) this.observer.disconnect();
      if (this.moreWrap) this.moreWrap.hidden = false;
      if (this.status) this.status.textContent = 'Nu am putut încărca mai multe produse. Încearcă din nou.';
    }
  }

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-sd-infinite]').forEach((root) => {
      if (root.sdInfinite) return;
      root.sdInfinite = new SdInfinite(root);
    });
  };

  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  // Grila de colecție e re-randată de filtrele Dawn (facets.js) => re-inițializăm noul markup.
  if ('MutationObserver' in window) {
    const hasGrid = (node) =>
      node.nodeType === 1 && (node.matches('[data-sd-infinite]') || node.querySelector('[data-sd-infinite]'));
    new MutationObserver((mutations) => {
      if (mutations.some((m) => Array.from(m.addedNodes).some(hasGrid))) initAll();
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
